import { addMinutes } from "date-fns";
import { match, Pattern } from "ts-pattern";
import { Ref } from "vue";

import controller from "@ctrl/controller";
import {
  Block,
  Controller,
  Document,
  EventPatchContent,
  EventTime,
  GraphDatabase,
  GraphResult,
  MandateDestination,
  MandateDocument,
  MetricsObjectSource,
  ReferenceId,
  RoutineMandateLabel,
  RoutineMandatePriority,
  TableDefaultExtensionsKind,
  Task,
  TaskRead,
} from "@ctrl/controller-rpc";

import {
  DEFAULT_EVENT_DURATION_MINUTES,
  type ConsoleDefaultValues,
} from "@/components/console/default";
import { getUrl } from "@/components/context";
import {
  getBuiltinKind,
  makeDefaultParentObjectId,
} from "@/components/databases";
import { createBlockParagraph } from "@/components/document";
import {
  contextToGraphResult,
  findContextField,
} from "@/components/graph/context";
import type { GraphRelationResult } from "@/components/graph/graph-relation";
import ModalConsole from "@/components/ModalConsole.vue";
import { State } from "@/components/parsed-input";
import { LABELS_TABLE_ID } from "@/components/tables";
import { withTaskDefaultCalendar } from "@/composables/useCalendars";
import { useExtendedProperties } from "@/composables/useExtendedProperties";
import { getDefaultDatabase, type UseProfile } from "@/composables/useProfile";
import { logger } from "@/logger/renderer";
import { RouteNames } from "@/routes/types";
import { ConsoleAction, Context, Router } from "@/types";
import { createAttachment } from "@/utils/attachments";
import { moveCustomObject } from "@/utils/hierarchy";
import { in_electron } from "@/utils/in";
import { mimeFromFileName } from "@/utils/media";
import { navigateTo, navigateToObject } from "@/utils/navigate";
import { joinPath } from "@/utils/path";

import {
  runQuery,
  setGraphProperty,
  setGraphPropertyRaw,
} from "./graph/component";

async function resolveTaskUrl(
  controller: Controller,
  profile: Ref<UseProfile>,
  context: Context | undefined
): Promise<string | undefined> {
  if (!context) return undefined;
  if (context.category === "file" && in_electron) {
    const fullPath = joinPath(context.path ?? "", context.fileName ?? "");
    if (!fullPath) return undefined;
    try {
      const fs = require("fs");
      const buffer = await fs.promises.readFile(fullPath);
      const bytes = new Uint8Array(
        buffer.buffer,
        buffer.byteOffset,
        buffer.byteLength
      );
      const fileName = context.fileName || require("path").basename(fullPath);
      const file = new File([bytes], fileName, {
        type: mimeFromFileName(fileName),
      });
      const attachment = await createAttachment(
        profile.value.user.personal.workspaceId,
        controller,
        file
      );
      return attachment.url;
    } catch {
      return undefined;
    }
  }
  return getUrl(context);
}

const { makeBlockId } = controller;

export function statePostpone(task: Task): State {
  return {
    escaped: `${task.title} `,
    mode: "postpone",
    task,
  };
}

export function stateSchedule(task: Task): State {
  return {
    escaped: `${task.title} `,
    task,
    mode: "schedule",
  };
}

export function statePlan(task: Task): State {
  return {
    escaped: `${task.title} `,
    task,
    text: "on ",
    mode: "plan",
  };
}

// The metrics source for all the console-related actions
const source: MetricsObjectSource = "console";

async function setLabelsOnExtensionRow(
  controller: Controller,
  extensionsDb: GraphDatabase,
  extensionRowId: string,
  labels: RoutineMandateLabel[]
): Promise<void> {
  if (labels.length === 0) return;
  const workspaceId = extensionsDb.workspace;

  const labelsDbRef = await controller.graph.database.get(
    workspaceId,
    LABELS_TABLE_ID
  );
  const labelsDb = labelsDbRef?.value ?? null;
  labelsDbRef?.release?.();
  if (!labelsDb) return;

  const relations: GraphRelationResult[] = labels.map((label) => ({
    _routine: { id: label.id, database: labelsDb },
  }));

  setGraphProperty(
    relations,
    "Labels",
    extensionRowId,
    extensionsDb,
    controller,
    undefined
  );
}

async function setTaskExtensionProperties(
  controller: Controller,
  profile: Ref<UseProfile>,
  taskId: string,
  priority: RoutineMandatePriority | undefined,
  labels: RoutineMandateLabel[] | undefined,
  pillExtensions?: Record<string, GraphResult>
): Promise<void> {
  const hasPillValues =
    pillExtensions && Object.keys(pillExtensions).length > 0;
  if (!priority && (!labels || labels.length === 0) && !hasPillValues) return;

  const ensureExtensionRow = useExtendedProperties(
    controller,
    profile,
    "task",
    () => null,
    () => {},
    async (extensionRowId) => {
      await controller.task.update(
        taskId,
        { extension_row: extensionRowId },
        "console",
        null
      );
    }
  );

  const extensionRowId = await ensureExtensionRow();
  if (!extensionRowId) return;

  const extensionsDb = getDefaultDatabase(profile.value, "task");
  if (!extensionsDb) return;

  // Pill-set extension values first; parsed keywords (`!priority`, `#label`)
  // are applied after so they win over a leftover pill value: the pill is
  // read-only while the keyword is present, so a value set before the keyword
  // was typed must not overwrite it.
  if (pillExtensions) {
    for (const [name, value] of Object.entries(pillExtensions)) {
      setGraphProperty(
        value,
        name,
        extensionRowId,
        extensionsDb,
        controller,
        undefined
      );
    }
  }

  if (priority) {
    setGraphPropertyRaw(
      `"${priority.name}"`,
      "Priority",
      extensionRowId,
      extensionsDb,
      controller,
      undefined
    );
  }

  if (labels && labels.length > 0) {
    setLabelsOnExtensionRow(controller, extensionsDb, extensionRowId, labels);
  }
}

// Apply pill-picked extension-column values (Labels, custom user columns, ...)
// and parsed `#label` keywords to a freshly created event/page through its
// extensions row, creating and linking the row first.
async function setExtensionPillProperties(
  controller: Controller,
  profile: Ref<UseProfile>,
  kind: TableDefaultExtensionsKind,
  link: (extensionRowId: string) => Promise<void>,
  extensions: Record<string, GraphResult> | undefined,
  labels?: RoutineMandateLabel[]
): Promise<void> {
  const hasExtensions = extensions && Object.keys(extensions).length > 0;
  const hasLabels = labels && labels.length > 0;
  if (!hasExtensions && !hasLabels) return;

  const ensureExtensionRow = useExtendedProperties(
    controller,
    profile,
    kind,
    () => null,
    () => {},
    link
  );
  const extensionRowId = await ensureExtensionRow();
  if (!extensionRowId) return;

  const extensionsDb = getDefaultDatabase(profile.value, kind);
  if (!extensionsDb) return;

  for (const [name, value] of Object.entries(extensions ?? {})) {
    setGraphProperty(
      value,
      name,
      extensionRowId,
      extensionsDb,
      controller,
      undefined
    );
  }

  if (hasLabels) {
    await setLabelsOnExtensionRow(
      controller,
      extensionsDb,
      extensionRowId,
      labels
    );
  }
}

// Plain pill text turned into a paragraphs-only document (task/event notes).
const notesDocument = (text: string): Document => ({
  blocks: text.split("\n").map((line) => createBlockParagraph(line)),
});

// Resolve the Calendar pill's value (a calendar summary) back to a calendar
// id, preferring writable calendars when summaries collide.
const resolveCalendarId = async (
  controller: Controller,
  summary: string
): Promise<string | undefined> => {
  const calendars = (await controller.calendar.all()).release() ?? [];
  const matching = calendars.filter((c) => c.summary === summary);
  return (matching.find((c) => c.isWritable) ?? matching[0])?.id;
};

/**
 * The event time computed from the Date, Duration, and All day pills. The
 * pills display the defaults (today, `DEFAULT_EVENT_DURATION_MINUTES`) when
 * unset, so an unset pill resolves to the same value here:
 * - all day → a whole-day event on the date (today when unset);
 * - date + duration → starts on that day at the current wall-clock time;
 * - date only → same, with the default duration;
 * - duration only → starts now;
 * - neither → the caller's default (a default-length draft starting now).
 */
const eventTimeFromDefaults = (
  defaults: ConsoleDefaultValues
): EventTime | undefined => {
  const { date, durationMinutes } = defaults;
  if (defaults.allDay) return { date: date ?? new Date() };
  if (date === undefined && durationMinutes === undefined) return undefined;
  const now = new Date();
  const start = date ? new Date(date) : now;
  if (date) start.setHours(now.getHours(), now.getMinutes(), 0, 0);
  return {
    start_time: start,
    end_time: addMinutes(
      start,
      durationMinutes ?? DEFAULT_EVENT_DURATION_MINUTES
    ),
  };
};

// Post-creation patch for pill values the event RPCs don't take at creation
// time (or that arrive after `console.performAction` created the event).
const eventPatchFromDefaults = (
  defaults: ConsoleDefaultValues
): EventPatchContent => {
  const patch: EventPatchContent = {};
  if (defaults.location !== undefined) patch.location = defaults.location;
  if (defaults.description !== undefined)
    patch.description = defaults.description;
  if (defaults.notes !== undefined) patch.notes = notesDocument(defaults.notes);
  if (defaults.active !== undefined)
    patch.attendance = defaults.active ? "attend" : "ignore";
  if (defaults.busy !== undefined)
    patch.availability_override = defaults.busy ? "busy" : "free";
  if (defaults.participants && defaults.participants.length > 0)
    patch.attendees = defaults.participants.map((email) => ({ email }));
  return patch;
};

// Apply the event pills and parsed labels to a created event: patch content
// fields and set extension values. Fire-and-forget consistency with the other
// console writes.
const applyEventDefaults = async (
  controller: Controller,
  profile: Ref<UseProfile>,
  eventId: string,
  defaults: ConsoleDefaultValues | null,
  labels?: RoutineMandateLabel[]
): Promise<void> => {
  const patch = defaults ? eventPatchFromDefaults(defaults) : {};
  if (Object.keys(patch).length > 0) {
    await controller.event.update({ id: eventId, ...patch }, false, source);
  }
  await setExtensionPillProperties(
    controller,
    profile,
    "event",
    async (extensionRowId) => {
      await controller.event.update(
        { id: eventId, extension_row: extensionRowId },
        false,
        source
      );
    },
    defaults?.extensions,
    labels
  );
};

// Apply the page pills and parsed labels to a created page: append the notes
// to the page body and set extension values.
const applyPageDefaults = async (
  controller: Controller,
  profile: Ref<UseProfile>,
  pageId: string,
  defaults: ConsoleDefaultValues | null,
  labels?: RoutineMandateLabel[]
): Promise<void> => {
  if (defaults?.notes !== undefined) {
    for (const line of defaults.notes.split("\n")) {
      await controller.document.append(
        { id: { kind: "page", id: pageId } },
        createBlockParagraph(line)
      );
    }
  }
  await setExtensionPillProperties(
    controller,
    profile,
    "page",
    async (extensionRowId) => {
      await controller.page.update(
        pageId,
        { extension_row: extensionRowId },
        source
      );
    },
    defaults?.extensions,
    labels
  );
};

// A document destination (picked through the console's destination pill or
// parsed from `>`) as a task-creation document, filing the new task into it.
const documentFromDestination = (
  destination: MandateDestination | undefined
): MandateDocument | undefined =>
  destination && destination.type === "document"
    ? {
        id: destination.id,
        ...(destination.section ? { section: destination.section } : {}),
      }
    : undefined;

// What `performAction` receives from its caller, threaded down to the
// per-action creators below.
type ActionInject = {
  controller: Controller;
  profile: Ref<UseProfile>;
  context?: Context;
  // Extra property values (keyed by property name) to apply on creation,
  // currently only for custom-type objects (from the console pills).
  properties?: Record<string, GraphResult>;
  // Pill values for a default type (task/event/page) creation, applied
  // through the type-specific RPCs rather than graph properties.
  defaults?: ConsoleDefaultValues;
  // Parsed `>` redirection target. For custom-type creation the new object is
  // linked into this destination (and parented under it when it is an object).
  destination?: MandateDestination;
  // Parsed `#label` keywords (from the query's mandate), applied to the
  // created object's extensions row. Only consumed by the creators whose
  // action carries no labels of its own (page, event, and the pills-alone
  // task); the task actions ship their labels in the action payload.
  labels?: RoutineMandateLabel[];
};

// Task pill values applied at task creation; the parsed action's own
// scheduling (postponing/scheduling/allocation) always wins over the pill.
const taskDefaultsFrom = (
  defaults: ConsoleDefaultValues | undefined
): Partial<TaskRead> =>
  defaults?.kind === "task"
    ? {
        ...(defaults.starred !== undefined
          ? { starred: defaults.starred }
          : {}),
        ...(defaults.notes !== undefined
          ? { notes: notesDocument(defaults.notes) }
          : {}),
      }
    : {};

// Creation helpers, in two tiers:
// - `create<X>FromAction(action, title, inject)`: one per `performAction`
//   match arm, consuming the payload of the parsed action;
// - `create<X>(title, inject)`: the plain creators working from the console
//   pills alone, used when the action carries no payload of its own
//   (`page-creation`, and the `custom-type` action whose type resolves to a
//   built-in kind).

// Create an event from a parsed `event-creation` action, folding the event
// pills into it.
async function createEventFromAction(
  action: Extract<ConsoleAction, { type: "event-creation" }>,
  title: string,
  inject: ActionInject
): Promise<ReferenceId | null> {
  const { controller, defaults } = inject;
  // The Calendar pill can only take effect at creation time (an event's
  // calendar isn't patchable), so resolve it into the action first.
  let withDefaults = action;
  if (defaults?.kind === "event") {
    if (defaults.calendar) {
      const calendarId = await resolveCalendarId(controller, defaults.calendar);
      if (calendarId) withDefaults = { ...withDefaults, calendar: calendarId };
    }
    // An explicitly pill-set date replaces the parsed one (the pill is
    // read-only while a date keyword is present).
    if (defaults.date) withDefaults = { ...withDefaults, date: defaults.date };
    // A pill-set duration adjusts the event span when the action has a
    // start time (a duration alone can't place an event); action spans
    // are in seconds.
    if (defaults.durationMinutes !== undefined && withDefaults.daytime)
      withDefaults = {
        ...withDefaults,
        daytime: [withDefaults.daytime[0], defaults.durationMinutes * 60],
      };
    // All day drops the time span: a date-only creation is a whole-day
    // event.
    if (defaults.allDay) {
      const { daytime: _daytime, ...rest } = withDefaults;
      withDefaults = rest;
    }
  }
  const id = await controller.console.performAction(title, withDefaults);
  if (
    id &&
    id.kind === "event" &&
    (defaults?.kind === "event" || inject.labels?.length)
  ) {
    await applyEventDefaults(
      controller,
      inject.profile,
      id.id,
      defaults?.kind === "event" ? defaults : null,
      inject.labels
    );
  }
  return id;
}

// Create a task from one of the parsed task actions (plain creation,
// allocation, postponing, or scheduling), applying the task pills.
async function createTaskFromAction(
  action: Extract<
    ConsoleAction,
    {
      type: "task" | "task-allocation" | "task-postponing" | "task-scheduling";
    }
  >,
  title: string,
  inject: ActionInject
): Promise<ReferenceId | null> {
  const { controller, defaults } = inject;
  const taskDefaults = taskDefaultsFrom(defaults);
  const url = action.capture
    ? await resolveTaskUrl(controller, inject.profile, inject.context)
    : undefined;
  // The parsed `>` keyword already lands in `action.document`, so it
  // wins; otherwise fall back to a destination picked through the
  // console's destination pill.
  const document =
    action.document ?? documentFromDestination(inject.destination);
  const taskId = await match(action)
    .with({ type: "task-allocation" }, async (action) =>
      controller.task.createAllocate(
        {
          title,
          url,
          ...taskDefaults,
        },
        // Allocate in the task default calendar when the query did not name
        // a calendar itself.
        await withTaskDefaultCalendar(
          controller,
          inject.profile.value.user.accountId,
          action
        ),
        { ...action, document, source }
      )
    )
    .otherwise((action) =>
      controller.task.create(
        {
          title,
          url,
          scheduled:
            action.type === "task-postponing"
              ? action.batch
              : action.type === "task-scheduling"
                ? action.date
                : defaults?.kind === "task"
                  ? defaults.plannedFor
                  : undefined,
          ...taskDefaults,
        },
        { ...action, document, notify: true, source }
      )
    );
  if (taskId) {
    setTaskExtensionProperties(
      controller,
      inject.profile,
      taskId,
      action.priority,
      action.labels,
      defaults?.kind === "task" ? defaults.extensions : undefined
    );
  }
  return taskId ? ({ kind: "task", id: taskId } satisfies ReferenceId) : null;
}

// Create a page and apply the page pills.
async function createPage(
  title: string,
  inject: ActionInject
): Promise<ReferenceId> {
  const { controller, defaults, labels } = inject;
  const id = await controller.page.create(title, null, source);
  const pageDefaults = defaults?.kind === "page" ? defaults : null;
  if (pageDefaults || labels?.length) {
    await applyPageDefaults(
      controller,
      inject.profile,
      id,
      pageDefaults,
      labels
    );
  }
  return { kind: "page", id } satisfies ReferenceId;
}

// Create a task from the console pills alone (planned-for date, destination,
// extensions), through the task RPCs. A capture stores the context as the
// task url, like the backend's capturable task actions.
async function createTask(
  title: string,
  inject: ActionInject,
  capture = false
): Promise<ReferenceId> {
  const { controller, defaults } = inject;
  const url = capture
    ? await resolveTaskUrl(controller, inject.profile, inject.context)
    : undefined;
  const id = await controller.task.create(
    {
      title,
      url,
      scheduled: defaults?.kind === "task" ? defaults.plannedFor : undefined,
      ...taskDefaultsFrom(defaults),
    },
    {
      document: documentFromDestination(inject.destination),
      notify: true,
      source,
    }
  );
  // The custom-type action carries no labels of its own, so the parsed
  // `#label` keywords come from the mandate through `inject`.
  setTaskExtensionProperties(
    controller,
    inject.profile,
    id,
    undefined,
    inject.labels,
    defaults?.kind === "task" ? defaults.extensions : undefined
  );
  return { kind: "task", id } satisfies ReferenceId;
}

// Create an event from the console pills alone (calendar, date, duration,
// ...). Created as a draft, like every console/dashboard event creation.
async function createEvent(
  title: string,
  inject: ActionInject
): Promise<ReferenceId | null> {
  const { controller, defaults } = inject;
  const eventDefaults = defaults?.kind === "event" ? defaults : null;
  const pickedCalendarId = eventDefaults?.calendar
    ? await resolveCalendarId(controller, eventDefaults.calendar)
    : undefined;
  const defaultCal = pickedCalendarId
    ? null
    : (await controller.calendar.defaultCal()).release();
  const calendar = pickedCalendarId ?? defaultCal?.id;
  if (!calendar) return null;
  const now = new Date();
  const time = (eventDefaults && eventTimeFromDefaults(eventDefaults)) ?? {
    start_time: now,
    end_time: addMinutes(now, DEFAULT_EVENT_DURATION_MINUTES),
  };
  const patch = eventDefaults ? eventPatchFromDefaults(eventDefaults) : {};
  const id = await controller.event.create({
    title,
    calendar,
    draft: true,
    time,
    attendees: patch.attendees ?? [],
    ...(patch.location !== undefined ? { location: patch.location } : {}),
    ...(patch.description !== undefined
      ? { description: patch.description }
      : {}),
    ...(patch.notes ? { notes: patch.notes } : {}),
    ...(patch.attendance ? { attendance: patch.attendance } : {}),
    ...(patch.availability_override
      ? { availability_override: patch.availability_override }
      : {}),
  });
  if (eventDefaults || inject.labels?.length) {
    await setExtensionPillProperties(
      controller,
      inject.profile,
      "event",
      async (extensionRowId) => {
        await controller.event.update(
          { id, extension_row: extensionRowId },
          false,
          source
        );
      },
      eventDefaults?.extensions,
      inject.labels
    );
  }
  return { kind: "event", id } satisfies ReferenceId;
}

// Create a row of a custom type as a generic graph row, with the context
// field and pill-picked property values, and apply the `>` redirection.
async function createCustomObject(
  database: GraphDatabase,
  withContext: boolean,
  title: string,
  inject: ActionInject
): Promise<ReferenceId | null> {
  const { controller } = inject;
  const contextField = withContext ? findContextField(database) : undefined;
  const contextValue =
    contextField && inject.context
      ? await contextToGraphResult(
          controller,
          database.workspace,
          inject.context
        )
      : undefined;
  const extraProperties = inject.properties ?? {};
  // When the console pills contributed property values, create the row
  // through the generic `rows add properties` form (same path the graph
  // create modals use) so all field values are written in one go.
  const row_id =
    Object.keys(extraProperties).length > 0
      ? await runQuery(
          database.workspace,
          `databases.[databaseId].rows add properties`,
          {
            databaseId: database.id,
            properties: {
              Title: title,
              ...(contextField && contextValue
                ? { [contextField]: contextValue }
                : {}),
              ...extraProperties,
            },
          },
          controller
        )
      : contextField && contextValue
        ? await runQuery(
            database.workspace,
            `databases.[dbId].rows add { Title: title, ${contextField}: contextValue }`,
            {
              dbId: database.id,
              title,
              contextValue,
            },
            controller
          )
        : await runQuery(
            database.workspace,
            `databases.[dbId].rows add { Title: title }`,
            { dbId: database.id, title },
            controller
          );
  if (row_id === undefined) return null;
  const id = await controller.graph.object.id.make({
    row_id,
    workspace_id: database.workspace,
    table_id: database.id,
  });
  // Apply the destination (parsed `>` redirection or the destination pill).
  // Destinations go through `moveCustomObject`, which records the parent (custom
  // object id, or a built-in parent encoded via makeDefaultParentObjectId)
  // and writes the reference block into the destination's notes —
  // `controller.document.append` alone would leave no record of where the
  // block lives (and is a no-op for custom object destinations). Only the
  // kinds without a graph table (recurrent tasks/events) fall back to a bare
  // append with no parent recorded. Calendar destinations don't apply to
  // custom objects and are ignored.
  const destination = inject.destination;
  if (destination && destination.type === "document") {
    const parentId =
      destination.id.kind === "object"
        ? destination.id.id
        : await makeDefaultParentObjectId(
            controller,
            inject.profile.value.user.personal.workspaceId,
            destination.id.kind,
            destination.id.id
          );
    if (parentId) {
      await moveCustomObject(
        controller,
        logger.renderer.wrapper(),
        database.workspace,
        { tableId: database.id, rowId: row_id },
        parentId,
        source
      );
    } else {
      const objectBlock: Block = {
        id: makeBlockId(),
        type: "object",
        workspace_id: database.workspace,
        table_id: database.id,
        row_id,
      };
      await controller.document.append(destination, objectBlock);
    }
  }
  return { kind: "object", id } satisfies ReferenceId;
}

// Create an object of the type carried by a `custom-type` action: built-in
// types are created through their dedicated RPCs rather than a generic graph
// row.
async function createCustomObjectFromAction(
  action: Extract<ConsoleAction, { type: "custom-type" }>,
  title: string,
  inject: ActionInject
): Promise<ReferenceId | null> {
  const { database, context: withContext } = action;
  const builtinKind = getBuiltinKind(database);
  // Only a task (context stored as its url) or a custom type with a context
  // property can capture; the action list never offers the capture variant
  // for pages and events.
  if (builtinKind === "task") return createTask(title, inject, withContext);
  if (builtinKind === "page") return createPage(title, inject);
  if (builtinKind === "event") return createEvent(title, inject);
  return createCustomObject(database, withContext, title, inject);
}

export async function performAction(
  action: ConsoleAction,
  title: string,
  inject: ActionInject
): Promise<ReferenceId | null> {
  const { controller } = inject;
  match(action)
    .with(
      { type: "event-allocation" },
      { type: "task-allocation" },
      (action) => {
        const ids = action.participants?.map((p) =>
          match(p)
            .with(Pattern.string, (email) => email)
            .with({ email: Pattern.select() }, (email) => email)
            .exhaustive()
        );
        if (ids) controller.people.useEmails(ids!);
      }
    )
    .otherwise(() => {});
  // FIXME: This should eventually entirely be handled by the
  // controller.console.performAction.
  return await match(action)
    .with({ type: "event-allocation" }, async (action) => {
      const eventId = await controller.event.allocate(title, action, source);
      return eventId
        ? ({ kind: "event", id: eventId } satisfies ReferenceId)
        : null;
    })
    .with({ type: "event-creation" }, (action) =>
      createEventFromAction(action, title, inject)
    )
    .with(
      { type: "task" },
      { type: "task-allocation" },
      { type: "task-postponing" },
      { type: "task-scheduling" },
      (action) => createTaskFromAction(action, title, inject)
    )
    .with({ type: "task-recurrence" }, async (action) => {
      const taskId = await controller.taskRecurrent.create(
        {
          title,
          ...action,
        },
        { document: action.document, notify: true }
      );
      return taskId
        ? ({ kind: "task-rec", id: taskId } satisfies ReferenceId)
        : null;
    })
    .with({ type: "page-creation" }, () => createPage(title, inject))
    .with({ type: "task-move" }, (action) =>
      action.document ? action.document.id : null
    )
    .with({ type: "recurrent-task-move" }, (action) =>
      action.document ? action.document.id : null
    )
    .with({ type: "custom-type" }, (action) =>
      createCustomObjectFromAction(action, title, inject)
    )
    .exhaustive();
}

export const toggleConsole = async (
  controller: Controller,
  profile: Ref<UseProfile>,
  router: Router,
  console: Ref<InstanceType<typeof ModalConsole> | null | undefined>
) => {
  if (in_electron) {
    const { ipcRenderer: ipc } = require("electron");
    ipc.send("open-dashboard");
  } else {
    const res = await console.value?.toggle();
    if (res) {
      const id = await performAction(res.action, res.title, {
        controller,
        profile,
        properties: res.properties,
        defaults: res.defaults,
        destination: res.query?.destination,
        labels: res.query?.labels,
      });
      if (res.options?.isOpeningItem) {
        if (typeof id === "string") {
          if (res.action.type === "custom-type")
            await navigateTo(RouteNames.Object, router, {
              params: {
                id,
                database: res.action.database.name,
              },
            });
        } else await navigateToObject(router, controller, id);
      }
    }
  }
};
